import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import BaseTextArea from '../inputs/BaseTextArea';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A textarea form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseTextArea>} BaseTextAreaProps
 *
 * @param {BaseFieldRenderProps & BaseTextAreaProps} fieldRenderProps
 * @returns {React.ReactElement} Textarea form field.
 */
const FormTextArea = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    hint,
    disabled,
    optional,
    readOnly,
    wrapperClassName,
    placeholder = 'Type text here',
    Wrapper = FieldWrapper,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';

  return (
    <Wrapper className={wrapperClassName}>
      {label && (
        <Label
          editorId={editorId}
          editorValid={valid}
          optional={optional}
          {...(readOnly ? { className: 'label-readonly' } : {})}
        >
          {label}
        </Label>
      )}
      <BaseTextArea
        valid={valid}
        id={editorId}
        name={name}
        disabled={disabled}
        ariaDescribedBy={`${hintId} ${errorId}`}
        readOnly={readOnly}
        placeholder={placeholder}
        dataTestId={editorId}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </Wrapper>
  );
};

export default FormTextArea;
